import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import ReactFlow, {
  Background,
  Position,
  ReactFlowProvider,
  getSmoothStepPath,
  BaseEdge,
  EdgeLabelRenderer,
} from "reactflow";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toPng, getFontEmbedCSS } from "html-to-image";
import "reactflow/dist/style.css";
import repartisLogo from "../../assets/images/logo.svg";
import {
  buildFlowElements,
  columnX,
  nodeStyles,
  DEFAULT_DESTINATIONS_PER_PAGE,
  STANDARD_A4_FRAME,
} from "./diagramUtils";

const formatNumberCH = (value, options = {}) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }
  const numberValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numberValue)) {
    return value?.toString ? value.toString() : "";
  }
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = Math.max(minimumFractionDigits, 2),
  } = options;

  return numberValue.toLocaleString("de-CH", {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

// Custom edge component for transfer labels (right-angle)
const TransferEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) => {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 12,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: "#b1b1b7",
          strokeWidth: 2,
          // solid grey line (no dash)
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 18,
            fontWeight: 600,
            pointerEvents: "all",
          }}
          className="nodrag nopan"
        >
          <div
            style={{
              background: "#ffffff",
              border: "1.5px solid #3b82f6",
              borderRadius: "4px",
              padding: "6px 10px",
              whiteSpace: "nowrap",
            }}
          >
            {data?.label || ""}
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

// Custom edge component with HTML label for better PDF export
const LabeledEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, label, labelStyle, data }) => {
  // Calculate distance to determine routing
  const horizontalDistance = Math.abs(targetX - sourceX);
  
  // Determine if target is in Column 4 (destination2) - route between Column 3 and Column 4
  // Column 3 (buyer1) is at 780, Column 4 (destination2) is at 1030
  // Check column data - Column 4 destinations have column='right'
  const isColumn4 = data?.column === 'right';
  
  let edgePath;
  if (isColumn4) {
    // For Column 4 destinations, route through gap between Column 3 and Column 4 ONLY
    // Column 3 (buyer1) is at 780, Column 4 (destination2) is at 1030
    // The path must turn vertically BETWEEN Column 3 and Column 4, not between Column 2 and Column 3
    // Position waypoint to ensure the vertical turn happens in the gap between Column 3 and Column 4
    // Use a waypoint that's clearly past Column 3 (780) but before Column 4 (1030)
    // Route in the true gap between buyer1 right edge and destination2 left edge
    const buyerRightEdge = columnX.buyer1 + 160;
    const destination2LeftEdge = columnX.destination2;
    const waypointX = (buyerRightEdge + destination2LeftEdge) / 2;
    const borderRadius = 15;
    const isGoingDown = targetY > sourceY;
    
    // Create path that routes through the gap between Column 3 and Column 4 ONLY
    // Path structure: horizontal from source -> vertical turn at waypoint (between Column 3 and 4) -> horizontal to target
    // The waypoint at 967 ensures the vertical turn (and branching) happens clearly between Column 3 (780) and Column 4 (1030)
    // This prevents the lines from branching between Column 2 and Column 3
    if (isGoingDown) {
      edgePath = `M ${sourceX},${sourceY} ` +
                 `H ${waypointX - borderRadius} ` +
                 `Q ${waypointX},${sourceY} ${waypointX},${sourceY + borderRadius} ` +
                 `V ${targetY - borderRadius} ` +
                 `Q ${waypointX},${targetY} ${waypointX + borderRadius},${targetY} ` +
                 `H ${targetX}`;
    } else {
      edgePath = `M ${sourceX},${sourceY} ` +
                 `H ${waypointX - borderRadius} ` +
                 `Q ${waypointX},${sourceY} ${waypointX},${sourceY - borderRadius} ` +
                 `V ${targetY + borderRadius} ` +
                 `Q ${waypointX},${targetY} ${waypointX + borderRadius},${targetY} ` +
                 `H ${targetX}`;
    }
  } else {
    // For other destinations, use standard routing
    const borderRadius = horizontalDistance > 500 ? 25 : 12;
    [edgePath] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: borderRadius,
    });
  }

  // Position label near the target (surface/consumption) box
  // Calculate position closer to target node
  const offsetX = -40; // Offset to the left of target
  const offsetY = -4; // Vertical offset with 2px spacing from the line

  // Force uniform grey color for all edges
  const edgeColor = "#b1b1b7";

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: edgeColor,
          strokeWidth: 2,
          strokeOpacity: 1,
          visibility: "visible",
          display: "block",
          fill: "none",
        }}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(${targetX + offsetX}px, ${targetY + offsetY}px)`,
              fontSize: labelStyle?.fontSize || 20,
              fontWeight: labelStyle?.fontWeight || 600,
              pointerEvents: "none",
              zIndex: 1000,
              color: "#00033F",
            }}
            className="edge-label-html nodrag nopan"
          >
            <span
              style={{
                background: "transparent",
                whiteSpace: "nowrap",
                fontFamily: '"PP Neue Montreal", sans-serif',
              }}
            >
              {label}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Custom edge component for straight lines between destinations and buyers/targets
const StraightEdge = ({ id, sourceX, sourceY, targetX, targetY }) => {
  const midY = (sourceY + targetY) / 2;
  const edgePath = `M ${sourceX},${midY} L ${targetX},${midY}`;
  
  return (
    <BaseEdge
      id={id}
      path={edgePath}
      style={{
        stroke: "#b1b1b7",
        strokeWidth: 2,
      }}
    />
  );
};


const formatPeriodRange = (keyData) => {
  if (!keyData) return "-";
  const { start_date: startDate, end_date: endDate } = keyData;
  if (!startDate && !endDate) return "-";
  try {
    const format = (value) =>
      value
        ? new Date(value).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "-";
    return `${format(startDate)} - ${format(endDate)}`;
  } catch {
    return "-";
  }
};

// CSS properties needed to render a ReactFlow diagram correctly.
// toPng normally reads ALL ~400 computed properties per element — limiting to these
// ~80 gives a ~5x speedup while still covering everything needed for accurate layout.
// CRITICAL: box-sizing must be included or ReactFlow nodes render at wrong sizes
// (border-box vs content-box mismatch shifts handle positions).
const DIAGRAM_STYLE_PROPS = [
  'box-sizing',
  'display','visibility','opacity','overflow','overflow-x','overflow-y',
  'position','top','left','right','bottom','z-index',
  'width','height','min-width','min-height','max-width','max-height',
  'margin','margin-top','margin-right','margin-bottom','margin-left',
  'padding','padding-top','padding-right','padding-bottom','padding-left',
  'border','border-radius','border-color','border-width','border-style',
  'border-top','border-right','border-bottom','border-left',
  'border-top-width','border-right-width','border-bottom-width','border-left-width',
  'border-top-style','border-right-style','border-bottom-style','border-left-style',
  'border-top-color','border-right-color','border-bottom-color','border-left-color',
  'border-top-left-radius','border-top-right-radius','border-bottom-left-radius','border-bottom-right-radius',
  'outline','outline-color','outline-style','outline-width','outline-offset',
  'background','background-color','background-image','background-position','background-size','background-repeat',
  'box-shadow',
  'color','font','font-size','font-weight','font-family','font-style','font-variant',
  'line-height','letter-spacing','word-spacing',
  'text-align','text-decoration','text-transform','text-overflow','white-space','word-break','word-wrap',
  'vertical-align',
  'transform','transform-origin','transform-style',
  'flex','flex-direction','flex-wrap','flex-grow','flex-shrink','flex-basis',
  'align-items','align-content','align-self','justify-content','justify-items','justify-self',
  'gap','row-gap','column-gap','order',
  'stroke','stroke-width','stroke-dasharray','stroke-linecap','stroke-linejoin','stroke-opacity',
  'fill','fill-opacity','fill-rule',
  'marker-start','marker-mid','marker-end',
  'pointer-events','cursor','user-select',
];

const exportLog = (msg) => {
  const ts = new Date().toLocaleTimeString('en-GB', { hour12: false });
  console.log(`[PDF Export ${ts}] ${msg}`);
};

const KeyDiagram = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const backTarget = returnTo && decodeURIComponent(returnTo).startsWith("/reports") ? decodeURIComponent(returnTo) : "/reports";
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const pageFrameRefs = useRef([]);
  const isAdmin = JSON.parse(localStorage.getItem("user") || "{}")?.access_level === 1;

  useEffect(() => {
    fetchDiagram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDiagram = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}reports/keys/${id}`, {
        headers: {
          "api-key": process.env.REACT_APP_API_KEY,
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (res.data.status === "success") {
        setReportData(res.data.data);
      } else {
        setError(res.data.message || "Unable to load diagram data.");
      }
    } catch (err) {
      console.error("Report diagram fetch error:", err);
      setError(err?.response?.data?.message || err.message || "Unable to load diagram data.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!pageFrameRefs.current?.length || !reportData) return;

    setExporting(true);
    exportLog('=== Single key export started ===');

    try {
      const pixelsToPoints = 72 / 96;
      const html2canvasScale = 4;
      let pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
      const pageWidthPt = pdf.internal.pageSize.getWidth();
      const pageHeightPt = pdf.internal.pageSize.getHeight();
      const margin = 24;

      const availablePages = pageFrameRefs.current.filter(Boolean);
      if (!availablePages.length) {
        alert("No diagram to export.");
        return;
      }

      exportLog(`Pages to export: ${availablePages.length}`);

      // Pre-fetch font CSS once. html-to-image normally re-fetches fonts on every
      // toPng call (two passes each time). By fetching once and passing fontEmbedCSS
      // to every call we get correct font rendering with only one slow fetch.
      exportLog('Pre-fetching font CSS (runs once)...');
      const tFont = Date.now();
      let fontEmbedCSS = '';
      try {
        fontEmbedCSS = await getFontEmbedCSS(availablePages[0]);
        exportLog(`Font CSS fetched in ${Date.now() - tFont}ms`);
      } catch (e) {
        exportLog(`Font CSS fetch failed (${e.message}), continuing without fonts`);
      }

      for (let pageIndex = 0; pageIndex < availablePages.length; pageIndex += 1) {
        const diagramElement = availablePages[pageIndex];
        const pageLabel = `page ${pageIndex + 1}/${availablePages.length}`;
        const t0 = Date.now();
        exportLog(`${pageLabel} — starting`);

        try {
        // Hide controls and minimap
        const controls = diagramElement.querySelector('.react-flow__controls');
        const minimap = diagramElement.querySelector('.react-flow__minimap');
        if (controls) controls.style.display = 'none';
        if (minimap) minimap.style.display = 'none';

        // Get the body container to ensure overflow is visible for cost boxes
        const diagramBody = diagramElement.querySelector('.diagram-report-body');
        const originalBodyOverflow = diagramBody ? diagramBody.style.overflow : '';
        if (diagramBody) {
          diagramBody.style.overflow = 'visible';
        }

        // Pre-render the React Flow renderer to a PNG using html-to-image (preserves on-screen colors)
        exportLog(`${pageLabel} — toPng start`);
        const tPng = Date.now();
        let flowPNGData = null;
        let flowPNGSize = { width: 0, height: 0 };
        try {
          const rendererEl = diagramElement.querySelector('.reactflow-capture-root')               || diagramElement.querySelector('.diagram-report-body .react-flow__viewport')               || diagramElement.querySelector('.react-flow__viewport')               || diagramElement.querySelector('.diagram-report-body .react-flow__renderer')               || diagramElement.querySelector('.react-flow__renderer')               || diagramElement.querySelector('.diagram-report-body')               || diagramElement;
          if (rendererEl) {
            const rect = rendererEl.getBoundingClientRect();
            flowPNGSize = { width: Math.ceil(rect.width), height: Math.ceil(rect.height) };
            // Temporarily force grey strokes directly on SVG paths for capture
            const __restore = [];
            try {
              const edgePaths = rendererEl.querySelectorAll('.react-flow__edge path, .react-flow__edge-path');
              edgePaths.forEach((p) => {
                const prev = {
                  stroke: p.getAttribute('stroke'),
                  strokeWidth: p.getAttribute('stroke-width'),
                  dash: p.getAttribute('stroke-dasharray'),
                  styleStroke: p.style.stroke,
                  styleStrokeWidth: p.style.strokeWidth,
                };
                __restore.push({ el: p, prev });
                const sw = String(parseFloat(prev.strokeWidth || '2') || 2);
                p.setAttribute('stroke', '#b1b1b7');
                p.setAttribute('stroke-width', sw);
                p.removeAttribute('stroke-dasharray');
                p.style.stroke = '#b1b1b7';
                p.style.strokeWidth = sw;
              });
            } catch {}

            try {
              flowPNGData = await toPng(rendererEl, {
                cacheBust: false,
                pixelRatio: 5,
                fontEmbedCSS,
                includeStyleProperties: DIAGRAM_STYLE_PROPS,
                width: rect.width,
                height: rect.height,
                backgroundColor: '#ffffff',
              });
            } finally {
              // Restore previous attributes/styles
              try {
                __restore.forEach(({ el, prev }) => {
                  if (prev.stroke === null) el.removeAttribute('stroke'); else el.setAttribute('stroke', prev.stroke);
                  if (prev.strokeWidth === null) el.removeAttribute('stroke-width'); else el.setAttribute('stroke-width', prev.strokeWidth);
                  if (prev.dash === null || prev.dash === undefined) el.removeAttribute('stroke-dasharray'); else el.setAttribute('stroke-dasharray', prev.dash);
                  el.style.stroke = prev.styleStroke || '';
                  el.style.strokeWidth = prev.styleStrokeWidth || '';
                });
              } catch {}
            }
          }
        } catch (e) {
          exportLog(`${pageLabel} — toPng FAILED (${e.message}), will use html2canvas fallback`);
        }

        exportLog(`${pageLabel} — toPng done in ${Date.now() - tPng}ms, flowPNGData=${!!flowPNGData}`);

        // If we have a rasterized flow image, composite header + flow + footer directly to A4 and skip full-page html2canvas
        if (flowPNGData) {
          const tComp = Date.now();
          try {
            const headerEl = diagramElement.querySelector('.diagram-report-header');
            const footerEl = diagramElement.querySelector('.diagram-report-footer');

            // Use toPng (with includeStyleProperties) for header/footer — same optimisation as flow.
            // html2canvas was taking ~23s here because it fetches images via XHR with useCORS
            // and the logo request was timing out. toPng embeds images inline and is not affected.
            exportLog(`${pageLabel} — toPng header/footer start`);
            const tHF = Date.now();
            const toPngOpts = { pixelRatio: 4, fontEmbedCSS, cacheBust: false, includeStyleProperties: DIAGRAM_STYLE_PROPS, backgroundColor: '#ffffff' };
            const [headerPNG, footerPNG] = await Promise.all([
              headerEl ? toPng(headerEl, toPngOpts).catch(() => null) : Promise.resolve(null),
              footerEl ? toPng(footerEl, toPngOpts).catch(() => null) : Promise.resolve(null),
            ]);
            exportLog(`${pageLabel} — toPng header/footer done in ${Date.now() - tHF}ms`);

            const loadImg = (src) => src ? new Promise((res) => {
              const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src;
            }) : Promise.resolve(null);
            const [headerImg, footerImg, flowImg] = await Promise.all([
              loadImg(headerPNG), loadImg(footerPNG), loadImg(flowPNGData),
            ]);

            const pageScale = 4;
            const cw = Math.floor(pageWidthPt * pageScale);
            const ch = Math.floor(pageHeightPt * pageScale);
            const marginPx = Math.floor(24 * pageScale);
            const comp = document.createElement('canvas');
            comp.width = cw; comp.height = ch;
            const ctx = comp.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, cw, ch);

            let y = marginPx;
            if (headerImg) {
              const sc = Math.min((cw - marginPx * 2) / headerImg.width, 1);
              const w = Math.floor(headerImg.width * sc);
              const h = Math.floor(headerImg.height * sc);
              ctx.drawImage(headerImg, Math.floor((cw - w) / 2), y, w, h);
              y += h + Math.floor(8 * pageScale);
            }

            const img = flowImg;

            let footerH = 0;
            if (footerImg) {
              const sc = Math.min((cw - marginPx * 2) / footerImg.width, 1);
              footerH = Math.floor(footerImg.height * sc) + Math.floor(8 * pageScale);
            }
            const availableH = ch - marginPx - footerH - y;

            if (img) {
              const sFlow = Math.min((cw - marginPx * 2) / img.width, availableH / img.height);
              ctx.drawImage(img, Math.floor((cw - img.width * sFlow) / 2), y,
                Math.floor(img.width * sFlow), Math.floor(img.height * sFlow));
            }

            if (footerImg) {
              const sc = Math.min((cw - marginPx * 2) / footerImg.width, 1);
              const w = Math.floor(footerImg.width * sc);
              const h = Math.floor(footerImg.height * sc);
              ctx.drawImage(footerImg, Math.floor((cw - w) / 2), ch - marginPx - h, w, h);
            }

            const pageImg = comp.toDataURL('image/jpeg', 0.95);
            if (pageIndex > 0) pdf.addPage('a4', 'portrait');
            pdf.addImage(pageImg, 'JPEG', 0, 0, pageWidthPt, pageHeightPt, undefined, 'FAST');

            // Restore controls/minimap
            if (controls) controls.style.display = '';
            if (minimap) minimap.style.display = '';
            if (diagramBody) diagramBody.style.overflow = originalBodyOverflow;

            exportLog(`${pageLabel} — composite done in ${Date.now() - tComp}ms | total ${Date.now() - t0}ms`);
            continue;
          } catch (e) {
            exportLog(`${pageLabel} — composite FAILED (${e.message}), falling back to full html2canvas`);
          }
        }

        exportLog(`${pageLabel} — fallback html2canvas start`);

        // CRITICAL: Read stroke colors from ORIGINAL element BEFORE cloning
        // This preserves the blue (#3b82f6) and red (#ff4444) colors
        // Use path data or parent edge ID as key for reliable matching
        const originalEdgePaths = diagramElement.querySelectorAll('.react-flow__edge path');
        const edgeColorMap = new Map();
        originalEdgePaths.forEach((path) => {
          const computedStyle = window.getComputedStyle(path);
          const stroke = computedStyle.stroke || path.getAttribute('stroke') || path.style.stroke;
          const strokeWidth = computedStyle.strokeWidth || path.getAttribute('stroke-width') || '2';
          
          // Use the path's d attribute (path data) as a unique key
          const pathData = path.getAttribute('d');
          if (pathData && stroke && stroke !== 'none' && stroke !== 'rgba(0, 0, 0, 0)' && stroke !== 'transparent') {
            edgeColorMap.set(pathData, { stroke, strokeWidth });
          }
        });

        // Capture diagram at its natural displayed size
        // Persist body dimensions for the cloned DOM
        try {
          const bodyEl = diagramElement.querySelector('.diagram-report-body');
          if (bodyEl) {
            const br = bodyEl.getBoundingClientRect();
            bodyEl.setAttribute('data-export-w', String(Math.ceil(br.width || bodyEl.clientWidth || 0)));
            bodyEl.setAttribute('data-export-h', String(Math.ceil(br.height || bodyEl.clientHeight || 0)));
          }
        } catch (e) {}

        diagramElement.classList.add('diagram-exporting');

        const canvas = await html2canvas(diagramElement, {
          backgroundColor: "#ffffff",
          scale: html2canvasScale,
          useCORS: true,
          allowTaint: true,
          logging: false,
          removeContainer: false,
          onclone: (clonedDoc) => {
            // If we have a pre-rendered PNG of the React Flow area, inject it and skip stroke overrides
            try {
              if (flowPNGData) {
                const body = clonedDoc.querySelector('.diagram-report-body');
                if (body) {
                  const bw = parseInt(body.getAttribute('data-export-w') || '0', 10) || flowPNGSize.width;
                  const bh = parseInt(body.getAttribute('data-export-h') || '0', 10) || flowPNGSize.height;
                  body.style.width = bw + 'px';
                  body.style.height = bh + 'px';
                  body.style.maxHeight = bh + 'px';
                  body.style.overflow = 'visible';
                  body.style.position = 'relative';
                  body.style.padding = body.style.padding || '0px 32px';
                  body.style.backgroundColor = '#f7f8ff';
                }
                const clonedRenderer = clonedDoc.querySelector('.reactflow-capture-root')
                  || clonedDoc.querySelector('.diagram-report-body .react-flow__viewport')
                  || clonedDoc.querySelector('.react-flow__viewport')
                  || clonedDoc.querySelector('.diagram-report-body .react-flow__renderer')
                  || clonedDoc.querySelector('.react-flow__renderer')
                  || clonedDoc.querySelector('.diagram-report-body');
                if (clonedRenderer) {
                  while (clonedRenderer.firstChild) clonedRenderer.removeChild(clonedRenderer.firstChild);
                  clonedRenderer.style.position = 'relative';
                  clonedRenderer.style.overflow = 'visible';
                  const img = clonedDoc.createElement('img');
                  img.src = flowPNGData;
                  img.style.position = 'absolute';
                  img.style.left = '0';
                  img.style.top = '0';
                  img.style.width = flowPNGSize.width + 'px';
                  img.style.height = flowPNGSize.height + 'px';
                  img.width = flowPNGSize.width;
                  img.height = flowPNGSize.height;
                  img.style.display = 'block';
                  img.style.visibility = 'visible';
                  clonedRenderer.appendChild(img);
                }
                // Ensure header/footer width then return to avoid color/stroke overrides
                const header = clonedDoc.querySelector('.diagram-report-header');
                if (header) header.style.setProperty('width', '100%', 'important');
                const footer = clonedDoc.querySelector('.diagram-report-footer');
                if (footer) footer.style.setProperty('width', '100%', 'important');
                return;
              }
            } catch {}
            // Inject CSS to force edge visibility
            const style = clonedDoc.createElement('style');
            style.textContent = `
              .react-flow__edges { opacity: 1 !important; visibility: visible !important; display: block !important; overflow: visible !important; }
              .react-flow__edge { opacity: 1 !important; visibility: visible !important; display: block !important; overflow: visible !important; }
              .react-flow__edge path { opacity: 1 !important; visibility: visible !important; display: block !important; stroke-width: 2px !important; }
              svg { opacity: 1 !important; visibility: visible !important; display: block !important; }
              path { opacity: 1 !important; visibility: visible !important; }
            `;
            clonedDoc.head.appendChild(style);
            
            // Ensure header and footer are 100% width
            const header = clonedDoc.querySelector('.diagram-report-header');
            if (header) {
              header.style.setProperty('width', '100%', 'important');
            }
            
            const footer = clonedDoc.querySelector('.diagram-report-footer');
            if (footer) {
              footer.style.setProperty('width', '100%', 'important');
            }
            
            // Ensure diagram body has visible overflow for cost boxes
            const clonedBody = clonedDoc.querySelector('.diagram-report-body .react-flow__renderer') || clonedDoc.querySelector('.react-flow__renderer') || clonedDoc.querySelector('.diagram-report-body');
            if (clonedBody) {
              clonedBody.style.setProperty('overflow', 'visible', 'important');
            }
            
            // Ensure React Flow viewport has visible overflow
            const reactFlowViewport = clonedDoc.querySelector('.react-flow__viewport');
            if (reactFlowViewport) {
              reactFlowViewport.style.setProperty('overflow', 'visible', 'important');
            }
            
            // Ensure all React Flow edges container is visible first
            const edgesContainer = clonedDoc.querySelector('.react-flow__edges');
            if (edgesContainer) {
              edgesContainer.style.opacity = '1';
              edgesContainer.style.visibility = 'visible';
              edgesContainer.style.display = 'block';
              edgesContainer.style.overflow = 'visible';
              edgesContainer.style.pointerEvents = 'none';
            }
            
            // Ensure all React Flow edge elements are visible
            const allEdgeElements = clonedDoc.querySelectorAll('.react-flow__edge');
            allEdgeElements.forEach(edgeEl => {
              edgeEl.style.opacity = '1';
              edgeEl.style.visibility = 'visible';
              edgeEl.style.display = 'block';
              edgeEl.style.overflow = 'visible';
            });
            
            // CRITICAL: Force all edge strokes to simple grey to ensure consistent export
            // Match paths and override to grey regardless of original colors
            const edgePaths = clonedDoc.querySelectorAll('.react-flow__edge path');
            edgePaths.forEach((edgePath) => {
              // Force grey stroke
              const stroke = '#b1b1b7';
              const strokeWidth = edgePath.getAttribute('stroke-width') || '2';
              // CRITICAL: Set as SVG attribute FIRST (html2canvas reads attributes)
              edgePath.setAttribute('stroke', stroke);
              edgePath.setAttribute('stroke-width', strokeWidth);
              edgePath.setAttribute('fill', 'none');
              edgePath.setAttribute('opacity', '1');
              edgePath.setAttribute('visibility', 'visible');
              edgePath.removeAttribute('stroke-dasharray');
              
              // Also set in style for visual rendering
              edgePath.style.opacity = '1';
              edgePath.style.visibility = 'visible';
              edgePath.style.display = 'block';
              edgePath.style.stroke = stroke;
              edgePath.style.strokeWidth = strokeWidth;
              edgePath.style.fill = 'none';
            });
            
            // Fallback: Ensure ALL paths have visible grey strokes (in case some aren't in React Flow edges)
            const allPaths = clonedDoc.querySelectorAll('path');
            allPaths.forEach(path => {
              // Only process paths that should have strokes (in edges container or have stroke attribute)
              const hasStroke = path.getAttribute('stroke') || path.style.stroke;
              const inEdgesContainer = path.closest('.react-flow__edge') || path.closest('.react-flow__edges');
              
              if (hasStroke || inEdgesContainer) {
                // Force uniform grey stroke
                let strokeColor = '#b1b1b7';
                
                // Ensure path is visible with explicit SVG attributes
                path.setAttribute('stroke', strokeColor);
                const sw = path.getAttribute('stroke-width') || '2';
                path.setAttribute('stroke-width', sw);
                path.setAttribute('fill', 'none');
                path.setAttribute('opacity', '1');
                path.setAttribute('visibility', 'visible');
                path.removeAttribute('stroke-dasharray');
                
                path.style.opacity = '1';
                path.style.visibility = 'visible';
                path.style.display = 'block';
                path.style.stroke = strokeColor;
                path.style.strokeWidth = sw;
                path.style.fill = 'none';
              }
            });
            
            // Ensure edge labels are visible
            const edgeTexts = clonedDoc.querySelectorAll('.react-flow__edge-text');
            edgeTexts.forEach(text => {
              text.style.fill = '#000';
              text.style.opacity = '1';
              text.style.visibility = 'visible';
              text.style.display = 'block';
              text.setAttribute('opacity', '1');
              text.setAttribute('visibility', 'visible');
            });
            
            const htmlLabels = clonedDoc.querySelectorAll('.edge-label-html');
            htmlLabels.forEach(label => {
              label.style.opacity = '1';
              label.style.visibility = 'visible';
              label.style.display = 'block';
            });
            
            // Ensure all SVG elements in React Flow are visible and properly sized
            const svgElements = clonedDoc.querySelectorAll('svg');
            svgElements.forEach(svg => {
              svg.style.opacity = '1';
              svg.style.visibility = 'visible';
              svg.style.display = 'block';
              svg.style.overflow = 'visible';
              svg.setAttribute('opacity', '1');
              svg.setAttribute('visibility', 'visible');
              
              // Ensure SVG has proper dimensions
              const width = svg.getAttribute('width') || svg.style.width || '100%';
              const height = svg.getAttribute('height') || svg.style.height || '100%';
              if (width && height) {
                svg.setAttribute('width', width);
                svg.setAttribute('height', height);
              }
              
              // Ensure all paths in this SVG have visible strokes
              const svgPaths = svg.querySelectorAll('path');
              svgPaths.forEach(path => {
                // Ensure stroke is visible
                if (!path.getAttribute('stroke') || path.getAttribute('stroke') === 'none') {
                  path.setAttribute('stroke', '#b1b1b7');
                }
                if (!path.getAttribute('stroke-width') || path.getAttribute('stroke-width') === '0') {
                  path.setAttribute('stroke-width', '2');
                }
                path.setAttribute('opacity', '1');
                path.setAttribute('visibility', 'visible');
                path.setAttribute('fill', 'none');
                
                path.style.opacity = '1';
                path.style.visibility = 'visible';
                path.style.display = 'block';
              });
            });
            
            // Ensure edge markers are visible
            const markers = clonedDoc.querySelectorAll('marker');
            markers.forEach(marker => {
              marker.style.opacity = '1';
              marker.style.visibility = 'visible';
              marker.setAttribute('opacity', '1');
            });
          },
          ignoreElements: (element) => {
            return element.classList?.contains('react-flow__controls') || 
                   element.classList?.contains('react-flow__minimap');
          },
        });

        // Restore controls and minimap
        if (controls) controls.style.display = '';
        if (minimap) minimap.style.display = '';
        if (diagramBody) {
          diagramBody.style.overflow = originalBodyOverflow;
        }

        diagramElement.classList.remove('diagram-exporting');

        const imageData = canvas.toDataURL("image/jpeg", 0.95);

        // Calculate actual diagram dimensions from canvas
        const visualWidthPx = canvas.width / html2canvasScale;
        const visualHeightPx = canvas.height / html2canvasScale;

        const contentWidthPt = pageWidthPt - margin * 2;
        const contentHeightPt = pageHeightPt - margin * 2;
        const visualWidthPt = visualWidthPx * pixelsToPoints;
        const visualHeightPt = visualHeightPx * pixelsToPoints;

        const scale = Math.min(contentWidthPt / visualWidthPt, contentHeightPt / visualHeightPt);
        const finalWidth = visualWidthPt * scale;
        const finalHeight = visualHeightPt * scale;
        const offsetX = (pageWidthPt - finalWidth) / 2;
        const offsetY = (pageHeightPt - finalHeight) / 2;

        if (pageIndex > 0) {
          pdf.addPage('a4', 'portrait');
        }
        pdf.addImage(imageData, 'JPEG', offsetX, offsetY, finalWidth, finalHeight, undefined, 'FAST');
        exportLog(`${pageLabel} — fallback complete in ${Date.now() - t0}ms total`);
      } catch (err) {
        exportLog(`${pageLabel} — ERROR: ${err.message}`);
        console.error(`Error capturing diagram:`, err);
      }
      }

      exportLog(`Loop done. Saving PDF...`);
      const tSave = Date.now();
      if (pdf) {
        const fileName = `${reportData.key.key_name || "key-report"}-diagram.pdf`;
        pdf.save(fileName);
        exportLog(`PDF saved in ${Date.now() - tSave}ms — ${fileName}`);
      } else {
        alert("No diagram was successfully captured for export.");
      }
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("Unable to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const firstPageLayout = useMemo(
    () => buildFlowElements(reportData, null, { page: 0, destinationsPerPage: DEFAULT_DESTINATIONS_PER_PAGE }),
    [reportData]
  );
  const totalPages = firstPageLayout.totalPages || 1;
  const pageLayouts = useMemo(
    () =>
      Array.from({ length: totalPages }, (_, pageIndex) =>
        buildFlowElements(reportData, null, {
          page: pageIndex,
          destinationsPerPage: DEFAULT_DESTINATIONS_PER_PAGE,
        })
      ),
    [reportData, totalPages]
  );
  const buildingName = reportData?.key?.building_name || "-";
  const whiteLabellingEnabled = reportData?.key?.period_white_labelling === true;
  const customLogoUrl = reportData?.key?.period_logo || null;
  const periodLabel = useMemo(
    () => formatPeriodRange(reportData?.key),
    [reportData?.key?.start_date, reportData?.key?.end_date]
  );
  const exportDateLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    []
  );
  // Define edge types for ReactFlow
  const edgeTypes = useMemo(() => ({
    transfer: TransferEdge,
    labeled: LabeledEdge,
    straight: StraightEdge,
  }), []);

  // Suppress benign ResizeObserver loop error that Chrome sometimes emits
  useEffect(() => {
    const handler = (e) => {
      const msg = (e?.message || e?.reason?.message || '').toString();
      if (msg.includes('ResizeObserver loop completed with undelivered notifications')) {
        e.preventDefault?.();
        e.stopPropagation?.();
        e.stopImmediatePropagation?.();
      }
    };
    window.addEventListener('error', handler, true);
    window.addEventListener('unhandledrejection', handler, true);
    return () => {
      window.removeEventListener('error', handler, true);
      window.removeEventListener('unhandledrejection', handler, true);
    };
  }, []);

  return (
    <div className="admin-page">
      <div className="admin-page-title d-flex align-items-center justify-content-between">
        <div>
          <h1 className="page-section-title mb-1">Key Diagram</h1>
          <p className="text-muted mb-0">
            Visual representation of key destinations, transfers and buyers.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => navigate(backTarget)}>
            Back
          </button>
          <button className="btn btn-primary" onClick={handleExportPDF} disabled={exporting || loading}>
            {exporting ? "Exporting..." : "Export Diagram (PDF)"}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 mb-0">Loading diagram...</p>
        </div>
      ) : reportData ? (
        <>
          {exporting && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: 'white',
              }}
            >
              <div className="spinner-border text-light" role="status" style={{ marginBottom: '20px' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>Exporting PDF... Please wait</div>
            </div>
          )}
          <div className="key-card p-3 p-lg-4 mt-4">
            <KeySummary data={reportData} />
          </div>

          {isAdmin && (
          <div className="d-flex justify-content-end mt-3 mb-2">
            <button
              className="btn btn-primary"
              onClick={() => {
                if (reportData?.key?.id) {
                  navigate(`/create-key?edit=${reportData.key.id}`);
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Edit Key
            </button>
          </div>
          )}

            {pageLayouts.map((layout, pageIndex) => (
            <div
              key={`diagram-page-${pageIndex}`}
              ref={(el) => {
                pageFrameRefs.current[pageIndex] = el;
              }}
              className="diagram-a4-frame"
              style={{
                // Pinned width (see KeyDiagramView.js for full rationale) — a
                // percentage width flexes with scrollbar toggle → React Flow's
                // ResizeObserver re-projects nodes → boxes visibly shift on
                // scroll (client's "no longer aligned" complaint). Fixed px
                // keeps ReactFlow's container dimensions constant.
                width: `${STANDARD_A4_FRAME.widthPx}px`,
                height: `${STANDARD_A4_FRAME.minHeightPx}px`,
                margin: "0 auto 28px auto",
                backgroundColor: "#ffffff",
                padding: "0",
                boxSizing: "border-box",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <style>{`
                  .react-flow__node {
                    user-select: none;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                  }
                  .react-flow__edge {
                    user-select: none;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    overflow: visible !important;
                  }
                  .react-flow__edges {
                    overflow: visible !important;
                  }
                  .react-flow__viewport {
                    user-select: none;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                  }
                  .react-flow__renderer {
                    user-select: none;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                  }
                  .react-flow__node:focus,
                  .react-flow__node:focus-visible {
                    outline: none !important;
                    box-shadow: none !important;
                  }
                  .react-flow__node-selected {
                    outline: none !important;
                    box-shadow: none !important;
                  }
                  .diagram-exporting .diagram-node {
                    box-shadow: none !important;
                  }
                  .diagram-exporting .react-flow__edge-text,
                  .diagram-exporting .react-flow__edge-textbg {
                    opacity: 1 !important;
                    visibility: visible !important;
                    display: block !important;
                  }
                  .diagram-exporting svg .react-flow__edge-text,
                  .diagram-exporting svg .react-flow__edge-textbg {
                    opacity: 1 !important;
                    visibility: visible !important;
                  }
                  .edge-label-html {
                    opacity: 1 !important;
                    visibility: visible !important;
                    display: block !important;
                    z-index: 1000 !important;
                  }
                  .diagram-exporting .edge-label-html {
                    opacity: 1 !important;
                    visibility: visible !important;
                    display: block !important;
                    z-index: 1000 !important;
                  }
                  /* Ensure edges are visible */
                  .react-flow__edge path {
                    stroke: #b1b1b7 !important;
                    stroke-width: 2px !important;
                    fill: none !important;
                    opacity: 1 !important;
                    visibility: visible !important;
                    display: block !important;
                  }
                  /* Ensure edges to column 4 are visible */
                  .react-flow__edge {
                    opacity: 1 !important;
                    visibility: visible !important;
                    display: block !important;
                  }
                  .react-flow__edge-text {
                    fill: #000 !important;
                    font-size: 20px !important;
                    font-weight: 600 !important;
                    opacity: 1 !important;
                    visibility: visible !important;
                    display: block !important;
                  }
                  .react-flow__edge-textbg {
                    fill: #fff !important;
                    stroke: #b1b1b7 !important;
                    stroke-width: 1px !important;
                    opacity: 1 !important;
                    visibility: visible !important;
                    display: block !important;
                  }
                  /* Ensure edge labels are not hidden */
                  .react-flow__edge-label {
                    opacity: 1 !important;
                    visibility: visible !important;
                    display: block !important;
                  }
                  svg .react-flow__edge-text,
                  svg .react-flow__edge-textbg {
                    opacity: 1 !important;
                    visibility: visible !important;
                  }
                `}</style>
              <div
                className="diagram-report-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "16px 32px 8px 32px",
                  borderBottom: "2px solid rgba(0,3,63,0.1)",
                  width: "100%",
                  minWidth: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                  <div>
                    <div
                      style={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#00033F",
                        lineHeight: 1.2,
                      }}
                    >
                      {buildingName}
                    </div>
                    <div
                      style={{
                        fontSize: "22px",
                        fontWeight: 500,
                        color: "#4a4c6b",
                      }}
                    >
                      {periodLabel}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {!whiteLabellingEnabled ? (
                      <img src={repartisLogo} alt="Repartis" style={{ height: "48px", width: "auto" }} />
                    ) : customLogoUrl ? (
                      <img src={`${process.env.REACT_APP_STATIC_URL}${customLogoUrl}`} alt="Logo" style={{ height: "48px", width: "auto" }} />
                    ) : null}
                  </div>
              </div>
              <div
                className="diagram-report-body"
                style={{
                  flex: 1,
                  borderRadius: "16px",
                  backgroundColor: "#f7f8ff",
                  padding: "12px 32px",
                  margin: "8px 0",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <ReactFlowProvider>
                  <div className="reactflow-capture-root" style={{ width: '100%', height: '100%', overflow: 'visible', position: 'relative' }}>
                  <ReactFlow
                    onInit={(instance) => {
                      // Settle-aware re-fit (matches KeyDiagramView). fitView fits
                      // both axes incl. the x=1440 spacer so wide diagrams fit
                      // horizontally; no stale-fit right-edge clip, no Y drift.
                      const doFit = () =>
                        instance.fitView({
                          padding: 0.06,
                          includeHiddenNodes: true,
                          maxZoom: 2.0,
                          minZoom: 0.1,
                          duration: 0,
                        });
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          doFit();
                          requestAnimationFrame(() => requestAnimationFrame(doFit));
                        });
                      });
                    }}
                    nodes={layout.nodes}
                    edges={layout.edges}
                    edgeTypes={edgeTypes}
                    minZoom={0.1}
                    maxZoom={2.0}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    nodesFocusable={false}
                    edgesFocusable={false}
                    elementsSelectable={false}
                    panOnScroll={false}
                    zoomOnScroll={false}
                    preventScrolling={false}
                    style={{ width: "100%", height: "100%" }}
                    deleteKeyCode={null}
                    multiSelectionKeyCode={null}
                    defaultEdgeOptions={{
                      animated: false,
                      style: { stroke: '#b1b1b7', strokeWidth: 2 },
                    }}
                    fitView
                    fitViewOptions={{ padding: 0.06, minZoom: 0.1, maxZoom: 2.0 }}
                  >
                    <Background variant="dots" gap={24} color="#e5e7eb" />
                  </ReactFlow>
                  </div>
                </ReactFlowProvider>
              </div>
              <div
                className="diagram-report-footer"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 32px 12px 32px",
                  borderTop: "2px solid rgba(0,3,63,0.1)",
                  color: "#00033F",
                  fontWeight: 600,
                  width: "100%",
                  minWidth: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <span>{whiteLabellingEnabled ? "Powered by core repartis" : "www.repartis.ch"}</span>
                <span>{reportData.key?.key_name || ""}{totalPages > 1 ? ` — Page ${pageIndex + 1}/${totalPages}` : ""}</span>
                <span>
                  Exported: {exportDateLabel}
                </span>
              </div>
            </div>
            ))}
        </>
      ) : (
        <div className="text-center py-5">
          <p className="mb-2">No diagram data available.</p>
        </div>
      )}
    </div>
  );
};

const KeySummary = ({ data }) => {
  const { key, stats, costs } = data;
  const periodLabel = useMemo(() => {
    if (!key.start_date && !key.end_date) return "-";
    try {
      const start = key.start_date ? new Date(key.start_date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) : "-";
      const end = key.end_date ? new Date(key.end_date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) : "-";
      return `${start} - ${end}`;
    } catch {
      return "-";
    }
  }, [key.start_date, key.end_date]);

  return (
    <div className="row gy-3">
      <div className="col-lg-4">
        <h5 className="mb-3 fw-bold">Key Information</h5>
        <ul className="list-unstyled mb-0">
          <li className="mb-2">
            <strong>Name:</strong> <span className="ms-1">{key.key_name || "-"}</span>
          </li>
          <li className="mb-2">
            <strong>Building:</strong> <span className="ms-1">{key.building_name || "-"}</span>
          </li>
          <li className="mb-2">
            <strong>Period:</strong> <span className="ms-1">{periodLabel}</span>
          </li>
          <li>
            <strong>Formula:</strong>
            <div className="mt-1">
              <code className="d-inline-block p-2 rounded" style={{ 
                backgroundColor: "#fff5f7", 
                border: "1px solid #fce7f3",
                fontSize: "0.9rem",
                color: "#be185d",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap"
              }}>
                {key.formula || "-"}
              </code>
            </div>
          </li>
        </ul>
      </div>
      <div className="col-lg-4">
        <h5 className="mb-3 fw-bold">Stats</h5>
        <ul className="list-unstyled mb-0">
          <li className="mb-2">
            <strong>Total destinations:</strong> <span className="ms-1">{stats?.total_destinations || 0}</span>
          </li>
          <li className="mb-2">
            <strong>Assigned buyers:</strong> <span className="ms-1">{stats?.buyers_assigned || 0}</span>
          </li>
          <li className="mb-2">
            <strong>Outgoing transfers:</strong> <span className="ms-1">{stats?.outgoing_transfers || 0}</span>
          </li>
          <li>
            <strong>Incoming transfers:</strong> <span className="ms-1">{stats?.incoming_transfers || 0}</span>
          </li>
        </ul>
      </div>
      <div className="col-lg-4">
        <h5 className="mb-3 fw-bold">Costs ({costs.length})</h5>
        {costs.length === 0 ? (
          <p className="text-muted mb-0">No primary costs assigned.</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {costs.map((cost) => (
              <li key={cost.id} className="mb-2">
                <strong>{cost.cost_name || "Cost"}:</strong>{" "}
                <span className="ms-1">
                  {formatNumberCH(cost.value_without_vat || cost.cost_value || 0, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} CHF
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Removed local buildFlowElements - now using the one from diagramUtils.js
// This function is kept for reference but should not be used
function _buildFlowElements_OLD(reportData, onEditClick) {
  if (!reportData) {
    return { nodes: [], edges: [], diagramHeight: 900 };
  }

  const nodes = [];
  const edges = [];
  const spacingY = 140; // Increased spacing to prevent overlapping
  let destinations = reportData.destinations || [];
  
  // Determine if we should split into 5-column layout (threshold: 10+ destinations)
  // If 10 or fewer destinations, use original single-column layout
  const shouldSplitColumns = destinations.length > 10;
  const splitIndex = shouldSplitColumns ? Math.ceil(destinations.length / 2) : destinations.length;
  const maxRowsInColumn = shouldSplitColumns ? splitIndex : destinations.length;
  
  // Debug logging - always log to verify logic
  console.log(`[KeyDiagram] Destinations: ${destinations.length}, shouldSplit: ${shouldSplitColumns}, splitIndex: ${splitIndex}`);

  const rowCount = Math.max(destinations.length || 1, 1);
  const rootY = ((maxRowsInColumn - 1) * spacingY) / 2;

  // Collect all costs (direct costs + transfer costs)
  const allCosts = [];
  
  // Add direct costs (filter out costs with 0.00 value)
  if (reportData.costs && reportData.costs.length > 0) {
    reportData.costs.forEach((cost) => {
      const amount = parseFloat(cost.value_without_vat || cost.cost_value || cost.value_with_vat || 0);
      // Filter out costs with 0.00 value
      if (amount === 0 || amount === null || amount === undefined || isNaN(amount)) {
        return; // Skip this cost
      }
      
      allCosts.push({
        code: cost.cost_number || cost.cost_id || "",
        description: cost.cost_name || "Cost",
        amount: amount,
        type: "direct",
      });
    });
  }
  
  // Collect transfer costs from incoming transfers.
  // Aggregate by (source key, cost number, cost name): sum amounts across *destinations* only.
  // API can return duplicate rows per (source, cost, destination) e.g. with_vat and without_vat;
  // count each (source, cost, destination) once (prefer without_vat), then sum by (source, cost).
  const transferCostsList = [];
  const transferCostsByKey = new Map(); // key: `${sourceKeyName}|${costNumber}|${costName}` -> { ..., byDestination: Map<destKey, { amount, result_without_vat, result_with_vat }> }
  if (reportData.incoming_transfers && reportData.incoming_transfers.length > 0) {
    reportData.incoming_transfers.forEach((transfer, transferIdx) => {
      const calculationResults = Array.isArray(transfer.calculation_results) 
        ? transfer.calculation_results 
        : [];
      
      calculationResults.forEach((calcResult, calcIdx) => {
          const sourceKeyName = transfer.source_key_name || "Unknown";
          
          const amountRaw = calcResult.result_without_vat !== null && calcResult.result_without_vat !== undefined
            ? calcResult.result_without_vat
            : (calcResult.result_with_vat !== null && calcResult.result_with_vat !== undefined
              ? calcResult.result_with_vat
              : (calcResult.result_value !== null && calcResult.result_value !== undefined
                ? calcResult.result_value
                : null));
          const amount = amountRaw !== null && amountRaw !== undefined ? parseFloat(amountRaw) : 0;
          
          const costName = calcResult.cost_name || null;
          const costNumber = calcResult.cost_number != null ? String(calcResult.cost_number) : null;
          
          const destinationNumber =
            calcResult.consumption_number ||
            calcResult.surface_number ||
            "";
          const destinationType = calcResult.destination_type || "";
          const destinationName = 
            calcResult.consumption_name ||
            calcResult.surface_name ||
            calcResult.label ||
            "";
          
          if (amount === 0 || amount === null || amount === undefined || isNaN(amount)) {
            return;
          }
          
          const hasCostNumber = costNumber && costNumber !== "null" && costNumber !== "undefined" && costNumber.trim() !== "";
          const hasCostName = costName && costName !== "null" && costName !== "undefined" && costName.trim() !== "";
          
          let displayName = "";
          if (hasCostNumber && hasCostName) {
            displayName = `${sourceKeyName} / ${costNumber} - ${costName}`;
          } else if (hasCostName) {
            displayName = `${sourceKeyName} / ${costName}`;
          } else if (hasCostNumber) {
            displayName = `${sourceKeyName} / ${costNumber}`;
          } else {
            displayName = `${sourceKeyName} / Transfer`;
          }
          
          const destinationLabel = destinationNumber && destinationName
            ? `${destinationNumber} - ${destinationName}`
            : destinationNumber || destinationName || "";
          
          const calcId = calcResult.id || calcResult.calculation_id || '';
          const uniqueId = calcId 
            ? `transfer-${transferIdx}-${calcId}`
            : `transfer-${transferIdx}-calc-${calcIdx}`;
          
          const dedupeKey = `${sourceKeyName}|${costNumber ?? ''}|${costName ?? ''}`;
          const destKey = destinationNumber || `dest-${transferIdx}-${calcIdx}`;
          const addWithoutVat = calcResult.result_without_vat != null && !isNaN(parseFloat(calcResult.result_without_vat))
            ? parseFloat(calcResult.result_without_vat) : 0;
          const addWithVat = calcResult.result_with_vat != null && !isNaN(parseFloat(calcResult.result_with_vat))
            ? parseFloat(calcResult.result_with_vat) : 0;
          const hasWithoutVat = addWithoutVat > 0;

          let entry = transferCostsByKey.get(dedupeKey);
          if (entry) {
            const byDest = entry.byDestination || new Map();
            const existingDest = byDest.get(destKey);
            if (existingDest) {
              const existingHasWithoutVat = (Number(existingDest.result_without_vat) || 0) > 0;
              if (hasWithoutVat && !existingHasWithoutVat) {
                byDest.set(destKey, { amount, result_without_vat: addWithoutVat, result_with_vat: addWithVat });
              }
              return;
            }
            byDest.set(destKey, { amount, result_without_vat: addWithoutVat, result_with_vat: addWithVat });
            entry.byDestination = byDest;
            return;
          }
          const byDestination = new Map();
          byDestination.set(destKey, { amount, result_without_vat: addWithoutVat, result_with_vat: addWithVat });
          transferCostsByKey.set(dedupeKey, {
            id: uniqueId,
            code: costNumber || sourceKeyName,
            costName: costName,
            costNumber: costNumber,
            description: displayName,
            sourceKey: sourceKeyName,
            destinationNumber: destinationNumber,
            destinationName: destinationName,
            destinationLabel: destinationLabel,
            destinationType: destinationType,
            amount: amount,
            result_without_vat: addWithoutVat || calcResult.result_without_vat,
            result_with_vat: addWithVat || calcResult.result_with_vat,
            result_value: calcResult.result_value,
            type: "transfer",
            byDestination,
          });
        });
    });
    transferCostsByKey.forEach((entry) => {
      if (entry.byDestination && entry.byDestination.size > 0) {
        let sumAmount = 0;
        let sumWithoutVat = 0;
        let sumWithVat = 0;
        entry.byDestination.forEach((d) => {
          sumAmount += Number(d.amount) || 0;
          sumWithoutVat += Number(d.result_without_vat) || 0;
          sumWithVat += Number(d.result_with_vat) || 0;
        });
        entry.amount = sumAmount;
        entry.result_without_vat = sumWithoutVat;
        entry.result_with_vat = sumWithVat;
      }
      delete entry.byDestination;
    });
    transferCostsList.push(...transferCostsByKey.values());
  }
  
  transferCostsList.forEach((transferCost) => {
    allCosts.push(transferCost);
  });
  
  // Add cost details node at the top right, positioned to avoid overlap
  if (allCosts.length > 0) {
    // Position it at the top right, well above and to the right of all nodes
    // Position it above the diagram but within the viewport
    const topY = 0; // Above the first row
    const rightX = columnX.buyer2 + 240; // Position cost box to the right of all columns
    
    const costDetailsNode = {
      id: "cost-details",
      position: { x: rightX, y: topY },
      data: {
        label: (
          <div
            style={{
              padding: "12px",
              fontFamily: '"PP Neue Montreal", sans-serif',
              fontSize: "20px",
              color: "#00033F",
              background: "#ffffff",
              border: "1px solid #d8dee9",
              borderRadius: "8px",
              minWidth: "280px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {allCosts.map((cost, idx) => {
              const formattedAmount = formatNumberCH(cost.amount, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              
              // Build cost label - show cost number and name if available (for both direct and transfer costs)
              // For transfer costs: use description which already has format "{sourceKey} / {cost_number} - {cost_name}"
              let costLabel = "";
              if (cost.type === "transfer") {
                // For transfer costs, use the description which is already formatted as:
                // "{sourceKeyName} / {cost_number} - {cost_name}"
                costLabel = cost.description || `${cost.sourceKey || "Unknown"} / Transfer`;
              } else {
                // For direct costs
                if (cost.code && cost.description) {
                  costLabel = `${cost.code} - ${cost.description}`;
                } else if (cost.code) {
                  costLabel = cost.code;
                } else if (cost.description) {
                  costLabel = cost.description;
                } else {
                  costLabel = "Cost";
                }
              }
              
              return (
                <div
                  key={cost.id || `${cost.type}-${cost.code}-${idx}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: idx < allCosts.length - 1 ? "8px" : "0",
                    paddingBottom: idx < allCosts.length - 1 ? "8px" : "0",
                    borderBottom: idx < allCosts.length - 1 ? "1px solid #e0e0e0" : "none",
                    width: "100%",
                  }}
                >
                  <div 
                    style={{ 
                      flex: "1 1 auto",
                      textAlign: "left",
                      marginRight: "12px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <span style={{ color: "#00033F", fontWeight: 500 }}>
                      {costLabel}
                    </span>
                  </div>
                  <div 
                    style={{ 
                      flex: "0 0 auto",
                      textAlign: "right",
                      fontWeight: 600, 
                      color: "#00033F", 
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formattedAmount} CHF
                  </div>
                </div>
              );
            })}
          </div>
        ),
      },
      style: {
        background: "transparent",
        border: "none",
        padding: 0,
        width: "auto",
        height: "auto",
      },
      draggable: false,
      selectable: false,
    };
    nodes.push(costDetailsNode);
  }

  const rootId = `key-${reportData.key.id}`;
  nodes.push({
    id: rootId,
    position: { x: columnX.root, y: rootY },
    data: {
      label: (
        <NodeContent
          title={reportData.key.key_name}
          textColor="#00033F"
          textAlign="center"
          centerContent
        />
      ),
    },
    style: nodeStyles.root,
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  });

  const destinationPositions = new Map();
  // Store array of unique keys in the same order as destinations for later reference
  const destinationUniqueKeys = [];
  const destinationColumnMap = new Map(); // Track which column each destination is in
  
  destinations.forEach((destination, index) => {
    const uniqueKey = destination.id != null 
      ? `dest-${destination.id}`
      : `dest-idx-${index}`;
    
    destinationUniqueKeys[index] = uniqueKey;
    
    // Calculate Y position: if split, first half in left column, second half in right column
    let y, column;
    if (shouldSplitColumns) {
      if (index < splitIndex) {
        // First half: left column
        y = index * spacingY;
        column = 'left';
      } else {
        // Second half: right column (aligned with first half)
        y = (index - splitIndex) * spacingY;
        column = 'right';
      }
    } else {
      // Single column
      y = index * spacingY;
      column = 'left';
    }
    
    const destNodeId = uniqueKey;
    destinationPositions.set(uniqueKey, y);
    destinationColumnMap.set(uniqueKey, column);

    const surfaceNumber =
      destination.destination_number ||
      destination.destination_id ||
      "Surface";
    
    // DEBUG: Log destination data to help identify missing names
    if (!destination.destination_name && destination.destination_id) {
      console.log(`[KeyDiagram] Destination ${destination.destination_id} (${surfaceNumber}) missing name:`, {
        destination_id: destination.destination_id,
        destination_number: destination.destination_number,
        destination_name: destination.destination_name,
        destination_label: destination.destination_label,
        destination_type: destination.destination_type,
        destination_value: destination.destination_value
      });
    }
    
    // Extract surface name - try multiple fallback strategies
    let surfaceName = destination.destination_name;
    
    // If no name, try to extract from label (format: "444 - Surface Name (1,262 m²)" or "444 (1,262 m²)")
    if (!surfaceName && destination.destination_label) {
      const label = destination.destination_label;
      
      // Strategy 1: Try to match pattern like "444 - Surface Name" or "444 Surface Name" or "Surface Name (444)"
      // This handles cases like "444 - Bureau 202 H13-17 (1,262 m²)"
      let match = label.match(/\d+\s*[-–]\s*(.+?)(?:\s*\(|$)/i);
      if (match && match[1]) {
        const extracted = match[1].trim();
        // Make sure it's not just a number or "Surface"
        if (extracted && extracted !== 'Surface' && !/^\d+$/.test(extracted)) {
          surfaceName = extracted;
        }
      }
      
      // Strategy 2: If label has format "Surface Name (444)" try to extract
      if (!surfaceName) {
        match = label.match(/(.+?)\s*\(\s*\d+/);
        if (match && match[1]) {
          const extracted = match[1].trim();
          if (extracted && extracted !== 'Surface' && extracted !== surfaceNumber) {
            surfaceName = extracted;
          }
        }
      }
      
      // Strategy 3: Remove known patterns and extract remaining text
      if (!surfaceName) {
        // Remove number, value in parentheses, "m²" or "kWh"
        const cleaned = label
          .replace(new RegExp('^\\s*' + surfaceNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*[-–]?\\s*', 'gi'), '')
          .replace(/\s*\([^)]*\)\s*$/, '')
          .replace(/\s*\d+[\s,]?\d*\s*(m²|kWh|CHF)\s*/gi, '')
          .trim();
        
        if (cleaned && 
            cleaned !== 'Surface' && 
            cleaned !== surfaceNumber && 
            cleaned !== '-' &&
            !/^\d+$/.test(cleaned)) {
          surfaceName = cleaned;
        }
      }
    }
    
    // Strategy 4: Check ALL calculation results from incoming transfers for matching surface
    if ((!surfaceName || surfaceName === surfaceNumber) && reportData.incoming_transfers) {
      // Look through incoming transfers to find calculation results that match this destination
      for (const transfer of reportData.incoming_transfers) {
        if (transfer.calculation_results && Array.isArray(transfer.calculation_results)) {
          for (const calcResult of transfer.calculation_results) {
            // Check if this calculation result matches this destination by number or ID
            const calcSurfaceNumber = calcResult.surface_number || calcResult.consumption_number || calcResult.variables?.surface_number;
            const calcSurfaceName = calcResult.surface_name || calcResult.consumption_name || calcResult.variables?.surface_label;
            const calcSurfaceId = calcResult.variables?.surface_id || calcResult.surface_id;
            
            // Match by surface number or destination ID
            const matchesByNumber = calcSurfaceNumber && String(calcSurfaceNumber) === String(surfaceNumber);
            const matchesById = calcSurfaceId && String(calcSurfaceId) === String(destination.destination_id);
            const matchesByDestinationId = String(destination.destination_id) === String(surfaceNumber);
            
            if ((matchesByNumber || matchesById || matchesByDestinationId) && calcSurfaceName && calcSurfaceName.trim()) {
              surfaceName = calcSurfaceName.trim();
              break;
            }
            
            // Also check label field as last resort
            if ((matchesByNumber || matchesById || matchesByDestinationId) && !surfaceName && calcResult.label) {
              const labelMatch = calcResult.label.match(/\d+\s*[-–]\s*(.+?)(?:\s*\(|$)/i);
              if (labelMatch && labelMatch[1] && labelMatch[1].trim() !== 'Surface' && !/^\d+$/.test(labelMatch[1])) {
                surfaceName = labelMatch[1].trim();
                break;
              }
            }
          }
        }
        if (surfaceName && surfaceName !== surfaceNumber) break;
      }
    }
    
    // Strategy 5: Check if destination matches any direct calculation results (if available in reportData)
    // This would require reportData to have calculation results for direct costs, which might not be available
    
    // Final fallbacks - if we still don't have a name, check if we can derive one
    const destinationType = destination.destination_type?.toLowerCase() || "";
    const isConsumption = destinationType === "consumption";
    
    if (!surfaceName || surfaceName === surfaceNumber) {
      // For consumption destinations, NEVER use destination_key_name as the subtitle
      // The subtitle should be the actual consumption name (destination_name)
      // destination_key_name is only for target keys (green nodes on the right)
      if (isConsumption) {
        // For consumption: try destination_name (from backend)
        // The enrichment from incoming transfers should have already set surfaceName if available
        // But if not, try destination_name directly
        if (destination.destination_name && destination.destination_name.trim()) {
          surfaceName = destination.destination_name.trim();
        } else {
          // If still not found, show null (component will handle it)
          // Do NOT use destination_key_name for consumption
          surfaceName = null;
        }
      } else {
        // For surface/other types: can use destination_key_name as fallback
        if (destination.destination_key_name && destination.destination_key_name.trim()) {
          surfaceName = destination.destination_key_name.trim();
        }
        // Only use destination_type as last resort if it's meaningful and different from default
        else if (destination.destination_type && 
            destination.destination_type.toLowerCase() !== 'surface' &&
            destination.destination_type !== '-') {
          surfaceName = destination.destination_type;
        } else {
          // If we still don't have a name, set to null to not show subtitle
          // This way the node will just show the number as title
          surfaceName = null;
        }
      }
    }

    const destinationValueLabel =
      destination.destination_value !== null && destination.destination_value !== undefined
        ? formatNumberWithUnit(
            destination.destination_value,
            destination.destination_unit ||
              (destination.destination_type === "surface"
                ? "m²"
                : destination.destination_type === "consumption"
                ? "kWh"
                : "")
          )
        : "-";
    
    const nodeTitle = surfaceNumber;

    // Determine node style based on destination type
    const nodeStyle = isConsumption
      ? nodeStyles.consumption 
      : destinationType === "surface"
      ? nodeStyles.surface
      : nodeStyles.destination;

    // Determine X position based on column
    const destX = column === 'right' ? columnX.destination2 : columnX.destination;
    
    // Debug logging for split columns
    if (shouldSplitColumns && (index === 0 || index === splitIndex)) {
      console.log(`[KeyDiagram] Destination ${index}: column=${column}, x=${destX}, y=${y}`);
    }
    
    // For consumption nodes: same display order as surface nodes
    // 1. Number (title) - top
    // 2. Name (subtitle) - middle  
    // 3. Value (meta) - bottom
    // Always ensure subtitle is a string (not null) for consumption nodes so it renders
    const consumptionSubtitle = (surfaceName && surfaceName.trim() && surfaceName !== "-") 
      ? surfaceName.trim() 
      : (destination.destination_name && destination.destination_name.trim() 
          ? destination.destination_name.trim() 
          : "-");
    
    const nodeContentProps = isConsumption
      ? {
          title: nodeTitle,  // Consumption number (e.g., "306")
          subtitle: consumptionSubtitle,  // Consumption name (e.g., "2 appartements en attique")
          meta: destinationValueLabel !== "-" ? [destinationValueLabel] : [],  // Value (e.g., "19132 kWh")
          textColor: "#ffffff"
        }
      : {
          title: nodeTitle,
          subtitle: surfaceName,
          meta: destinationValueLabel !== "-" ? [destinationValueLabel] : [],
          textColor: "#ffffff"
        };
    
    const nodeToAdd = {
      id: destNodeId,
      type: 'default',
      position: { 
        x: destX, 
        y: y
      },
      data: {
        label: (
          <NodeContent
            {...nodeContentProps}
          />
        ),
      },
      style: {
        ...nodeStyle,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
    
    nodes.push(nodeToAdd);

    edges.push({
      id: `edge-${rootId}-${destNodeId}`,
      source: rootId,
      target: destNodeId,
      type: "labeled",
      label: `${destination.ponderation || 1}`,
      labelStyle: {
        fontSize: 20,
        fontWeight: 600,
      },
      data: {
        column: column, // Pass column info ('left' or 'right') to edge
      },
    });
  });

  // Create unique buyer/target nodes for each destination
  destinations.forEach((destination, index) => {
    // Get the unique key that was used when creating the destination node (same index)
    const uniqueKey = destinationUniqueKeys[index];
    if (!uniqueKey) {
      return; // Skip if this destination wasn't processed
    }
    
    const destNodeId = uniqueKey;  // uniqueKey already has 'dest-' prefix
    const destY = destinationPositions.get(uniqueKey) || 0;
    const destColumn = destinationColumnMap.get(uniqueKey) || 'left';
    
    // Determine buyer/target column based on destination column
    // If splitting (10+ destinations): Column 2 → Column 3, Column 4 → Column 5
    // If not splitting (≤10 destinations): All destinations → Single buyer/target column
    const buyerTargetX = shouldSplitColumns 
      ? (destColumn === 'right' ? columnX.buyer2 : columnX.buyer1)
      : columnX.buyer1; // Use single column for ≤10 destinations

    if (destination.buyer) {
      const buyerKey = `buyer-${destination.buyer.id}-${uniqueKey}`;
      
      nodes.push({
        id: buyerKey,
        position: { x: buyerTargetX, y: destY },
        data: {
          label: (
            <NodeContent
              title={`${destination.buyer.buyer_number || ""}`.trim()}
              subtitle={destination.buyer.name || "Buyer"}
              textColor="#00033F"
            />
          ),
        },
        style: nodeStyles.buyer,
        targetPosition: Position.Left,
      });

      edges.push({
        id: `edge-${destNodeId}-${buyerKey}`,
        source: destNodeId,
        target: buyerKey,
        type: "straight",
      });
    }

    if (destination.target_key) {
      const targetId = destination.target_key.id || destination.target_key.key_name;
      const nodeId = `target-${targetId}-${uniqueKey}`;
      
      nodes.push({
        id: nodeId,
        position: { x: buyerTargetX, y: destY + 50 },
        data: {
          label: (
            <NodeContent
              title={destination.target_key.key_name}
              textColor="#00033F"
              textAlign="center"
              centerContent
            />
          ),
        },
        style: nodeStyles.target,
        targetPosition: Position.Left,
      });

      edges.push({
        id: `edge-${destNodeId}-${nodeId}`,
        source: destNodeId,
        target: nodeId,
        type: "straight",
      });
    }
  });

  // Calculate height based on split columns
  let calculatedHeight;
  if (destinations.length > 0) {
    if (shouldSplitColumns) {
      // Height should be based on the taller column
      const leftColumnRows = splitIndex;
      const rightColumnRows = destinations.length - splitIndex;
      const maxRows = Math.max(leftColumnRows, rightColumnRows);
      calculatedHeight = (maxRows - 1) * spacingY + 50;
    } else {
      calculatedHeight = (destinations.length - 1) * spacingY + 50;
    }
  } else {
    calculatedHeight = 400;
  }
  const diagramHeight = Math.max(300, calculatedHeight);

  return { nodes, edges, diagramHeight };
}

const NodeContent = ({
  title,
  subtitle,
  cost,
  costDetails = [],
  meta = [],
  textColor = "#ffffff",
  textAlign = "left",
  centerContent = false,
  showEditIcon = false,
  onEditClick,
}) => {
  const dividerColor = "#ffffff";
  
  const dividerStyle = {
    margin: "6px 0",
    width: "100%",
    height: 0,
    border: "none",
    borderTop: "2px solid #ffffff",
    display: "block",
  };

  return (
    <div
      className="diagram-node"
      style={{
        wordWrap: "break-word",
        overflowWrap: "break-word",
        fontSize: "20px",
        maxHeight: "180px",
        overflowY: "auto",
        paddingRight: "4px",
        fontFamily: '"PP Neue Montreal", sans-serif',
        color: textColor,
        textAlign,
        display: centerContent ? "flex" : "block",
        flexDirection: centerContent ? "column" : undefined,
        justifyContent: centerContent ? "center" : undefined,
        alignItems: centerContent
          ? textAlign === "center"
            ? "center"
            : textAlign === "right"
            ? "flex-end"
            : "flex-start"
          : undefined,
        height: centerContent ? "100%" : "auto",
        minHeight: centerContent ? "100%" : "auto",
        width: "100%",
        position: "relative",
      }}
    >
      {showEditIcon && onEditClick && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEditClick();
          }}
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            background: "#ffffff",
            border: "1px solid #d8dee9",
            borderRadius: "4px",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            zIndex: 10,
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f5f5f5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#ffffff";
          }}
          title="Edit Key"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#00033F"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
      )}
      {cost && (
        <>
          <div
            style={{
              wordBreak: "break-word",
              lineHeight: "1.25",
              fontSize: "22px",
              fontWeight: 600,
              color: textColor,
              fontFamily: '"PP Neue Montreal", sans-serif',
              textAlign,
            }}
            title={cost}
          >
            {cost}
          </div>
          <div style={dividerStyle} />
        </>
      )}
      <div
        className="fw-bold"
        style={{
          wordBreak: "break-word",
          lineHeight: "1.25",
          fontSize: "22px",
          color: textColor,
          fontFamily: '"PP Neue Montreal", sans-serif',
          textAlign,
        }}
        title={title}
      >
        {title || "-"}
      </div>
      {subtitle && (
        <>
          <div style={dividerStyle} />
          <div
            style={{
              wordBreak: "break-word",
              lineHeight: "1.25",
              marginTop: "3px",
              fontSize: "20px",
              fontWeight: 500,
              color: textColor,
              fontFamily: '"PP Neue Montreal", sans-serif',
              textAlign,
            }}
            title={subtitle}
          >
            {subtitle}
          </div>
        </>
      )}
      {meta.length > 0 && (
        <>
          {(title || subtitle) && (
            <div style={dividerStyle} />
          )}
          <ul
            className="list-unstyled mb-0 mt-1"
            style={{
              lineHeight: "1.3",
              fontSize: "18px",
              color: textColor,
              fontFamily: '"PP Neue Montreal", sans-serif',
              textAlign,
            }}
          >
            {meta.map((item, idx) => (
              <li
                key={idx}
                style={{
                  wordBreak: "break-word",
                  color: textColor,
                  fontFamily: '"PP Neue Montreal", sans-serif',
                textAlign,
                }}
              >
                {item}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const formatNumberWithUnit = (value, unit) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numericValue = Number(value);
  const formatted = Number.isFinite(numericValue)
    ? formatNumberCH(numericValue, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : value;
  return unit ? `${formatted} ${unit}` : formatted;
};

export default KeyDiagram;

